import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { envSetting } from '../../constants/envSetting';
import { endpoints } from '../../constants/endpoints';
import { saveRequestTokenAction } from '../actions/shared';
import { USER_LOGOUT, USER_CLEAR } from '../types/user';
import { BASE_CLEAR } from '../types/base';
import { MESSAGE_CLEAR } from '../types/message';
import { PERSON_DATA_CLEAR } from '../types/personData';
import { STATIC_DATA_CLEAR } from '../types/staticData';
import { TIER_CLEAR } from '../types/tier';
import { httpStatusCode } from '../../constants/base';

/* Logic for implementing Authenticantion (via 'prepareHeaders') 
**  and reAuthentication (via 'baseQueryWithReauth) as explained in the documentacion:
*  https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#automatic-re-authorization-by-extending-fetchbasequery
*/

// Define baseQuery with 'headersFetch' (prepareHeaders)
const baseQuery = fetchBaseQuery({ 
	baseUrl: envSetting.wisenrollApi, 
	prepareHeaders: (headers, { getState }) => {
		const token = getState()?.user?.requestToken 
		if (!headers.get('token') && token) {
			headers.set('Authorization', `Bearer ${token}`);
		}
		if (!headers.get('Content-Type')) {
			headers.set('Content-Type', 'application/json');
		}
		const rawLang = getState()?.user?.actualLanguage || 'en';
		const acceptLang = (typeof rawLang === 'string') ? rawLang.trim().toLowerCase() : 'en';
		if (!headers.get('Accept-Language')) {
			headers.set('Accept-Language', acceptLang);
		}
		return headers;
	}
})

// Defining baseQueryWithReaAuth to handle token refresh if needed
const baseQueryWithReauth = async (args, api, extraOptions) => {
	let result = await baseQuery(args, api, extraOptions)
	if (result.error && (
				(result.error.status === httpStatusCode.FORBIDDEN || result.error.originalStatus === httpStatusCode.FORBIDDEN) ||
				(result.error.status === httpStatusCode.INTERNAL_SERVER_ERROR || result.error.originalStatus === httpStatusCode.INTERNAL_SERVER_ERROR)
			)
		) {
	  // try to get a new token
	  const refreshResult = await baseQuery({
			url: endpoints.loginToken,
			method: 'POST',
			body: {
				username: envSetting.apiUser,
				password: envSetting.apiPassword
			}
		}, api, extraOptions)
	  if (refreshResult?.data && refreshResult?.data.success) {
			// store the new token
			api.dispatch(saveRequestTokenAction(refreshResult?.data?.success?.token));
			// retry the initial query
			result = await baseQuery(args, api, extraOptions)
	  } else {
			// If you get here, it means your refresh token is invalid/expired
			// and the request for refreshing it has failed.
			// Force logout.
			localStorage.clear();
			api.dispatch({ type: USER_LOGOUT })
			api.dispatch({ type: USER_CLEAR });
			api.dispatch({ type: BASE_CLEAR });
			api.dispatch({ type: MESSAGE_CLEAR });
			api.dispatch({ type: PERSON_DATA_CLEAR });
			api.dispatch({ type: STATIC_DATA_CLEAR });
			api.dispatch({ type: TIER_CLEAR });
			window.location.href = '/';
	  }
	}
	return result
  }


export const we_api = createApi({
	reducerPath: 'we_api',
	baseQuery: baseQueryWithReauth,
	tagTypes: ['StaticData'],
	endpoints: (builder) => ({
		// Mutation Obtaing Token
		getLoginToken: builder.mutation({
			query: ({ username, password }) => ({
				url: endpoints.loginToken,
				method: 'POST',
				body: { username, password },
			})
		}),
		// Mutation Calling to customer IBV reports
		customerIBVReports: builder.mutation({
			query: (body) => ({
				url: endpoints.customerIBVReports,
				method: 'POST',
				body
			})
		}),
		// Queries for getting person base data or person base data with product request 
		personBaseData: builder.query({
			query: ({ userId, productRequestId }) => ({
				url: (productRequestId) ? endpoints.personBaseData(userId, productRequestId) : endpoints.personBaseDataWithoutPR(userId),
				method: 'GET',
			}),
		}),
		// Query for register ip log
		registerIpLog: builder.mutation({
			query: (body) => ({
				url: endpoints.registerIpLog,
				method: 'POST',
				body
			})
		}),
		// Query for getting static data
		staticData: builder.query({
			query: () => ({
				url: endpoints.staticData,
				method: 'GET',
			}),
			providesTags: ['StaticData'],
		}),
		// Mutation for calling to send-otp-token (for getting otp token)
		getOtpToken: builder.mutation({
			query: (body) => ({
				url: endpoints.getOtpToken,
				method: 'POST',
				body
			})
		}),
		// Mutation for login-otp
		LoginOtp: builder.mutation({
			query: (body) => ({
				url: endpoints.login,
				method: 'POST',
				body
			})
		}),
	}),
})

// Export hooks for usage in functional components
export const { 
	useCustomerIBVReportsMutation, usePersonBaseDataQuery, useStaticDataQuery, useRegisterIpLogMutation,
	useGetOtpTokenMutation, useLoginOtpMutation
} = we_api